/**
 * The strings the fleet's components ask for, answered without i18next.
 *
 * `WorkspaceSwitch` is written against `useTranslation()` because the fleet is
 * localised; this console is not, and its content is English throughout. The
 * choice the fleet's README leaves open is therefore taken here — no i18n, but
 * *not* silently: a hook that returned the key would put `workspaceSwitch.
 * selectWorkspace` on the screen and look like a translation that had not
 * loaded yet.
 *
 * So the keys are a closed set and `t` is typed to it. A component asking for a
 * string nobody wrote does not compile, which is the same protection a missing
 * translation file gives at runtime, one step earlier. When i18next does arrive
 * this module is what it replaces, and the keys are already the fleet's.
 */

/*
 * The values are the fleet's own English (`i18n/locales/en.ts` @ `c4387239`),
 * word for word, rather than a rewording of it — the strings are part of how
 * the copied components read, and a console that says "Sign out" where the
 * fleet says "Logout" is the same kind of drift as a button of its own shape.
 */
const STRINGS = {
  "workspaceSwitch.selectWorkspace": "Select Workspace",
  "workspaceSwitch.searchWorkspaces": "Search workspaces",
  "workspaceSwitch.noWorkspacesFound": "No workspaces found",
  "workspaceSwitch.current": "Current",
  "workspaceSwitch.addWorkspace": "Add Workspace",
  "sideBar.userNav.logout": "Logout",
  "sideBar.userNav.settings": "Settings",
  "settings.title": "Settings",
  "apiTokens.title": "API Tokens",
  "apiTokens.description": "Manage API tokens for programmatic access to your workspace",
  "apiTokens.createButton": "Create New Token",
  "apiTokens.noTokens": "No API tokens created yet",
  "apiTokens.table.name": "Name",
  "apiTokens.table.createdBy": "Created By",
  "apiTokens.table.created": "Created",
  "apiTokens.table.lastUsed": "Last Used",
  "apiTokens.table.actions": "Actions",
  "apiTokens.table.never": "Never",
  "apiTokens.create.title": "Create API Token",
  "apiTokens.create.description":
    "Enter a name for your new API token. The token will only be shown once.",
  "apiTokens.create.nameLabel": "Name",
  "apiTokens.create.namePlaceholder": "e.g., CI/CD Token",
  "apiTokens.create.createButton": "Create Token",
  "apiTokens.create.creating": "Creating...",
  "apiTokens.created.title": "API Token Created",
  "apiTokens.created.description":
    "Your API token has been created. Copy it now, as you won't be able to see it again.",
  "apiTokens.created.copyButton": "Copy to Clipboard",
  "apiTokens.created.done": "Done",
  "apiTokens.delete.title": "Delete API Token",
  "apiTokens.delete.description":
    "Are you sure you want to delete the API token '{{name}}'? This action cannot be undone.",
  "apiTokens.delete.confirm": "Delete",
  "apiTokens.delete.cancel": "Cancel",
  "apiTokens.messages.createError": "Failed to create API token",
  "apiTokens.messages.deleteError": "Failed to delete API token",
  "apiTokens.messages.copySuccess": "Token copied to clipboard",
  "apiTokens.messages.copyError": "Failed to copy token",
  "apiTokens.messages.nameRequired": "Please enter a token name",

  /*
   * Ours, and the four things `design/0003` says the tokens screen has to say
   * in words. The fleet has no equivalent — its keys are not a superset of a
   * screen that grants full access to a workspace of live websites — so these
   * are written in the fleet's own voice and namespaced beside its keys rather
   * than kept somewhere else, because they are read off the same screen.
   *
   * `deleteSuccess` from the fleet is *not* here: it is a toast, and this
   * console has no toasts (`LayoutNotice`). A revoked key changes state in the
   * table in front of the person, which the fleet's own row cannot show because
   * its listing drops revoked keys altogether.
   */
  "apiTokens.grants.title": "What a key can do",
  "apiTokens.grants.once": "The value is shown once. It cannot be read again — issue a new key instead.",
  "apiTokens.grants.fullAccess":
    "A key has full access to this workspace — the same as a signed-in member, with nothing to choose.",
  "apiTokens.grants.irreversible":
    "That includes deleting a site, which archives it and retires its address for good. Revoking the key afterwards stops the next call and does not bring the address back.",
  "apiTokens.grants.creator":
    "A key stops working when the person who created it loses access to this workspace — not when the workspace decides. An integration dies the day its author leaves.",
  /*
   * Ours as well, and for a case the fleet has no branch for: its screen never
   * draws a failed *listing* (`useApiTokensQueryOptions` is read with `isLoading`
   * alone). The two beside it — `createError`, `deleteError` — are the fleet's
   * own and are used the same way here: as what a failure says when it is not
   * the API talking, which a dropped connection is not.
   */
  "apiTokens.messages.loadError": "Failed to load API tokens",
  "apiTokens.table.expires": "Expires",
  "apiTokens.table.state": "State",
  "apiTokens.state.revoked": "Revoked",
  "apiTokens.state.expired": "Expired",
  "apiTokens.create.expiryLabel": "Expires",

  /*
   * The members and invites screen (`sitebrew-api#252`, M3 of design note
   * 0001). No fleet equivalent exists to copy — the reference CMS design has no
   * multi-member workspace — so these are written fresh, in the same plain,
   * direct voice the `apiTokens.grants.*` keys above already use for
   * something the fleet has no branch for either.
   */
  "members.title": "Members",
  "members.description": "Who belongs to this workspace, and who has been invited.",
  "members.table.name": "Name",
  "members.table.email": "Email",
  "members.table.role": "Role",
  "members.table.joined": "Joined",
  "members.table.actions": "Actions",
  "members.role.owner": "Owner",
  "members.role.member": "Member",
  "members.remove.confirm": "Remove",
  "members.remove.cancel": "Cancel",
  "members.remove.title": "Remove member",
  "members.remove.description": "Remove '{{name}}' from this workspace? They keep their own workspace — this only ends their membership in this one.",
  "members.messages.loadError": "Failed to load members",
  "members.messages.removeError": "Failed to remove member",

  "invites.title": "Pending invites",
  "invites.description": "Sent, not yet accepted.",
  "invites.noInvites": "No pending invites",
  "invites.inviteButton": "Invite member",
  "invites.table.email": "Email",
  "invites.table.role": "Role",
  "invites.table.invited": "Invited",
  "invites.table.expires": "Expires",
  "invites.table.actions": "Actions",
  "invites.revoke.confirm": "Revoke",
  "invites.revoke.cancel": "Cancel",
  "invites.revoke.title": "Revoke invite",
  "invites.revoke.description": "Withdraw the invite sent to '{{email}}'? The link in that e-mail will stop working.",
  "invites.create.title": "Invite a member",
  "invites.create.description":
    "They'll get an e-mail with a link to accept. If they don't have an account yet, they'll be asked to sign in first.",
  "invites.create.emailLabel": "Email",
  "invites.create.emailPlaceholder": "someone@example.com",
  "invites.create.roleLabel": "Role",
  "invites.create.inviteButton": "Send invite",
  "invites.create.sending": "Sending...",
  "invites.messages.loadError": "Failed to load invites",
  "invites.messages.createError": "Failed to send invite",
  "invites.messages.revokeError": "Failed to revoke invite",
  "invites.messages.emailRequired": "Please enter an e-mail address",
} as const;

export type StringKey = keyof typeof STRINGS;

/**
 * The fleet's `{{name}}` placeholders, filled.
 *
 * i18next does this and the copied components are written expecting it — the
 * revoke dialog names the key it is about. Left unfilled the question would read
 * "delete the API token '{{name}}'", which is the kind of thing that ships
 * because nothing fails.
 */
const fill = (text: string, vars: Record<string, string> | undefined) =>
  vars ? text.replace(/{{(\w+)}}/g, (whole, key: string) => vars[key] ?? whole) : text;

export const useTranslation = () => ({
  t: (key: StringKey, vars?: Record<string, string>) => fill(STRINGS[key], vars),
});
